import {ReactNode, createContext, useMemo, useState} from 'react'
import _ from 'lodash'
import {useNavigate} from 'react-router-dom'

export interface INavStateWrapperProps {
  children?: ReactNode
}

export interface INavStateContext<
  TState extends {[key: string]: any} = {[key: string]: any},
  TArgs extends IUrlParams = IUrlParams,
> {
  path: INavigateArgs<TState, TArgs>['path']
  searchParams: INavigateArgs<TState, TArgs>['searchParams']
  navState: TState
  patchNavState: React.Dispatch<React.SetStateAction<Partial<TState>>>
  navigate: (args: INavigateArgs<TState, TArgs>) => void
}

export interface IUrlParams {
  [key: string]: string
}

interface INavigateArgs<
  TState extends {[key: string]: any} = {[key: string]: any},
  TArgs extends IUrlParams = IUrlParams,
> {
  path?: string
  searchParams?: Partial<TArgs>
  state?: Partial<TState>
  clearState?: boolean
}

export const NavStateContext = createContext<INavStateContext>({
  path: '',
  searchParams: {},
  navState: {},
  navigate: () => {},
  patchNavState: () => {
    throw new Error('not implemented')
  },
})

export function NavStateContextWrapper(props: INavStateWrapperProps) {
  const {children} = props

  const navigate = useNavigate()

  const [navState, setNavState] = useState<IUrlParams>({})

  const searchParams = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(document.location.search))
  }, [document.location.search])

  function patchNavState(data: any) {
    const newNavState = {
      ...navState,
      ...data,
    }
    handleNavigate({state: newNavState})
  }

  function handleNavigate({path, searchParams, state, clearState}: INavigateArgs) {
    const _searchParams = {
      ...(clearState ? undefined : (
        Object.fromEntries(new URLSearchParams(document.location.search))
      )),
      ...searchParams,
    }
    const _path = _.chain([
      path || document.location.pathname,
      new URLSearchParams(
        _.chain(_searchParams || {})
          .toPairs()
          .filter(([_k, v]) => v !== undefined)
          .value() as any
      ).toString(),
    ])
      .compact()
      .join('?')
      .value()
    const _state =
      clearState ?
        {}
      : {
          ...navState,
          ...state,
        }
    if (
      document.location.pathname + document.location.search === _path &&
      _.isEqual(navState, _state)
    )
      return
    setNavState(_state)
    navigate(_path, {
      state: _state,
      replace: true,
    })
  }

  return (
    <NavStateContext.Provider
      value={{
        path: document.location.pathname,
        searchParams,
        navState,
        patchNavState,
        navigate: handleNavigate,
      }}
    >
      {children}
    </NavStateContext.Provider>
  )
}
